/**
 * Status-effect HUD (vanilla Gui.renderEffects / EffectsInInventory), in GUI px on its own
 * stage: beneficial effect icons in a row at the top right, harmful ones in a second row, each on
 * the 24x24 effect background (icon blinks in its last 10 s); absorption hearts above the health
 * row; and, while the inventory is open, the left-hand list with name and m:ss timer.
 *
 *   const fxHud = new EffectsHud(parent);
 *   fxHud.update(set, { absorption, armor, creative, inventoryOpen, tick })   every frame (DOM writes only on change)
 *
 * Icons are the real vanilla mob_effect sprites (public/mc/mob_effect/*.png).
 */

import { blindness, darkness, nausea, effectDef, effectLabel, formatDuration, FX, type ActiveEffect, type EffectSet } from '../game/effects/effects';
import { GuiStage, mcText, nineSlice, sprite } from './kit';

export interface EffectsHudState {
  /** Absorption health points (2 per heart). */
  absorption: number;
  /** Armor points: the absorption row sits above the armor row when there is any. */
  armor: number;
  creative: boolean;
  /** The survival / creative inventory screen is open. */
  inventoryOpen: boolean;
  /** Any screen (inventory, chest, ...) is open: the corner icons are hidden. */
  screenOpen?: boolean;
  tick: number;
}

/** FX id -> vanilla sprite under public/mc/mob_effect/ (18x18). */
const SPRITE_OF: Record<number, string> = {
  [FX.SPEED]: 'speed', [FX.SLOWNESS]: 'slowness', [FX.HASTE]: 'haste', [FX.MINING_FATIGUE]: 'mining_fatigue',
  [FX.STRENGTH]: 'strength', [FX.INSTANT_HEALTH]: 'instant_health', [FX.INSTANT_DAMAGE]: 'instant_damage',
  [FX.JUMP_BOOST]: 'jump_boost', [FX.REGENERATION]: 'regeneration', [FX.RESISTANCE]: 'resistance',
  [FX.FIRE_RESISTANCE]: 'fire_resistance', [FX.WATER_BREATHING]: 'water_breathing', [FX.INVISIBILITY]: 'invisibility',
  [FX.NIGHT_VISION]: 'night_vision', [FX.HUNGER]: 'hunger', [FX.WEAKNESS]: 'weakness', [FX.POISON]: 'poison',
  [FX.WITHER]: 'wither', [FX.ABSORPTION]: 'absorption', [FX.SLOW_FALLING]: 'slow_falling', [FX.BAD_OMEN]: 'bad_omen',
  [FX.DARKNESS]: 'darkness', [FX.NAUSEA]: 'nausea', [FX.BLINDNESS]: 'blindness',
  [FX.LEVITATION]: 'levitation',
};
const iconCache = new Map<number, HTMLCanvasElement>();
let iconsLoaded = false;
const iconWaiters: Array<() => void> = [];

/** Load every vanilla effect sprite once; resolves when all are drawn into their cached canvases. */
export function loadEffectIcons(): Promise<void> {
  if (iconsLoaded) return Promise.resolve();
  return Promise.all(Object.keys(SPRITE_OF).map((k) => new Promise<void>((res) => {
    const id = Number(k);
    const img = new Image();
    img.onload = () => { effectIcon(id).getContext('2d')!.drawImage(img, 0, 0, 18, 18); res(); };
    img.onerror = () => {
      // missing file: vanilla magenta/black missing-texture marker
      const g = effectIcon(id).getContext('2d')!;
      for (let y = 0; y < 2; y++) for (let x = 0; x < 2; x++) { g.fillStyle = (x + y) & 1 ? '#000' : '#f800f8'; g.fillRect(x * 9, y * 9, 9, 9); }
      res();
    };
    img.src = `mc/mob_effect/${SPRITE_OF[id]}.png`;
  }))).then(() => { iconsLoaded = true; for (const w of iconWaiters.splice(0)) w(); });
}

/** The vanilla 18x18 mob-effect sprite for an effect id (cached canvas, filled once loadEffectIcons resolves). */
export function effectIcon(id: number): HTMLCanvasElement {
  let c = iconCache.get(id);
  if (c) return c;
  c = document.createElement('canvas');
  c.width = c.height = 18;
  iconCache.set(id, c);
  return c;
}

export class EffectsHud {
  private readonly stage: GuiStage;
  private readonly bar: HTMLDivElement;
  private readonly list: HTMLDivElement;
  private readonly hearts: HTMLDivElement;
  /** Darkness effect: a pulsing full-screen vignette under the HUD (the world shaders have no darkening hook). */
  private readonly dark: HTMLDivElement;
  private darkOp = '';
  private readonly nause: HTMLDivElement;
  private nauseKey = '';
  private barKey = '';
  private listKey = '';
  private heartKey = '';

  constructor(parent: HTMLElement) {
    // above the container screens (50) so the effect list can sit beside the inventory panel
    this.stage = new GuiStage(parent, 51);
    this.stage.interactive = false;
    const mk = (): HTMLDivElement => {
      const d = document.createElement('div');
      d.style.cssText = 'position:absolute;left:0;top:0;pointer-events:none;';
      this.stage.content.appendChild(d);
      return d;
    };
    this.bar = mk(); this.list = mk(); this.hearts = mk();
    this.dark = document.createElement('div');
    this.dark.style.cssText = 'position:fixed;inset:0;pointer-events:none;z-index:4;opacity:0;background:radial-gradient(ellipse at center,rgba(0,0,0,0.5) 0%,rgba(0,0,0,0.92) 100%);';
    parent.appendChild(this.dark);
    // Nausea: a slow purple wobble over the view (vanilla warps the whole frame with the portal shader)
    this.nause = document.createElement('div');
    this.nause.style.cssText = 'position:fixed;inset:-6%;pointer-events:none;z-index:4;opacity:0;mix-blend-mode:multiply;background:radial-gradient(ellipse at center,rgba(120,60,150,0.35) 0%,rgba(80,20,110,0.75) 100%);';
    parent.appendChild(this.nause);
    void loadEffectIcons();
    iconWaiters.push(() => { this.barKey = ''; this.listKey = ''; });
  }

  /** GUI-px top of the icon rows: below the touch buttons and the fps/perf box when present. */
  private topOffset(): number {
    let css = 4;
    if (typeof matchMedia === 'function' && matchMedia('(pointer: coarse)').matches) css = 62;
    const perf = document.querySelector<HTMLElement>('[data-perf-hud]');
    if (perf && perf.style.display !== 'none') css = Math.max(css, Math.ceil(perf.getBoundingClientRect().bottom) + 4);
    return Math.ceil(css / this.stage.scale);
  }

  update(set: EffectSet, st: EffectsHudState): void {
    const W = this.stage.width, H = this.stage.height;
    const effects = set.list;
    // blindness closes the view in almost completely (vanilla: fog distance 5 -> 0 + black), darkness pulses
    const dk = Math.max(darkness(set, st.tick), blindness(set) * 0.97).toFixed(2);
    if (dk !== this.darkOp) { this.darkOp = dk; this.dark.style.opacity = dk; }
    const nz = nausea(set);
    const nk = nz > 0 ? `${nz.toFixed(2)}|${st.tick % 120}` : '';
    if (nk !== this.nauseKey) {
      this.nauseKey = nk;
      this.nause.style.opacity = String(nz * (0.45 + 0.35 * Math.sin(st.tick * 0.12)));
      this.nause.style.transform = nz > 0 ? `rotate(${(Math.sin(st.tick * 0.07) * 2.5 * nz).toFixed(2)}deg) scale(${(1 + 0.04 * Math.sin(st.tick * 0.09) * nz).toFixed(3)})` : '';
    }
    // --- top-right icons: beneficial row at y=1, harmful row below (vanilla 25 px apart) ---
    const key = `${st.inventoryOpen || st.screenOpen ? 1 : 0}|${this.topOffset()}|${W}|` + effects.map((e) => `${e.id}.${e.amp}.${e.ambient ? 1 : 0}.${e.dur > 200 ? 1 : (st.tick >> 1) & 1}`).join(',');
    if (key !== this.barKey) {
      this.barKey = key;
      this.bar.replaceChildren();
      if (!st.inventoryOpen && !st.screenOpen) {
        const top = this.topOffset();
        let good = 0, bad = 0;
        for (const e of effects) {
          const beneficial = effectDef(e.id)?.beneficial ?? true;
          const x = W - 2 - 25 * (beneficial ? ++good : ++bad);
          const y = top + (beneficial ? 0 : 34);
          this.bar.appendChild(sprite(`gui/sprites/hud/effect_background${e.ambient ? '_ambient' : ''}.png`, x, y, 24, 24));
          const ic = effectIcon(e.id);
          const el = document.createElement('canvas');
          el.width = el.height = 18;
          el.getContext('2d')!.drawImage(ic, 0, 0);
          const blink = e.dur <= 200 && ((st.tick >> 1) & 1) === 1;
          el.style.cssText = `position:absolute;left:${x + 3}px;top:${y + 3}px;width:18px;height:18px;image-rendering:pixelated;opacity:${blink ? 0.4 : 1};`;
          this.bar.appendChild(el);
          // duration text below icon, blinking in last 10 seconds
          const durationText = mcText(formatDuration(e.dur), { color: '#ffffff', shadow: true });
          durationText.style.cssText = `position:absolute;left:${x + Math.floor((24 - durationText.width) / 2)}px;top:${y + 25}px;width:${durationText.width}px;height:${durationText.height}px;image-rendering:pixelated;opacity:${blink ? 0.4 : 1};`;
          this.bar.appendChild(durationText);
        }
      }
    }
    // --- inventory list (left of the 176 px panel; compact 32 px rows when the window is narrow) ---
    const wide = (W - 176) / 2 >= 124;
    const lkey = st.inventoryOpen ? `${W}|${H}|` + effects.map((e) => `${e.id}.${e.amp}.${formatDuration(e.dur)}`).join(',') : '';
    if (lkey !== this.listKey) {
      this.listKey = lkey;
      this.list.replaceChildren();
      if (st.inventoryOpen && effects.length > 0) this.buildList(effects, W, H, wide);
    }
    // --- absorption hearts ---
    const abs = st.creative ? 0 : Math.ceil(st.absorption);
    const hkey = `${W}|${H}|${abs}|${st.armor > 0 ? 1 : 0}`;
    if (hkey !== this.heartKey) {
      this.heartKey = hkey;
      this.hearts.replaceChildren();
      const n = Math.ceil(abs / 2);
      const y = H - 39 - (st.armor > 0 ? 20 : 10);
      for (let i = 0; i < Math.min(10, n); i++) {
        const half = i * 2 + 1 >= abs;
        this.hearts.appendChild(sprite(`gui/sprites/hud/heart/container.png`, W / 2 - 91 + i * 8, y, 9, 9));
        this.hearts.appendChild(sprite(`gui/sprites/hud/heart/absorbing_${half ? 'half' : 'full'}.png`, W / 2 - 91 + i * 8, y, 9, 9));
      }
    }
  }

  private buildList(effects: readonly ActiveEffect[], W: number, H: number, wide: boolean): void {
    const rowH = Math.min(33, Math.floor((H - 2) / Math.max(1, effects.length)));
    const rw = wide ? 120 : 64;
    const x = Math.max(1, wide ? Math.floor((W - 176) / 2) - 124 : Math.floor((W - 176) / 2) - 66);
    effects.forEach((e, i) => {
      const y = 1 + i * rowH;
      this.list.appendChild(nineSlice('gui/sprites/container/inventory/effect_background.png', x, y, rw, 32, 4));
      const el = document.createElement('canvas');
      el.width = el.height = 18;
      el.getContext('2d')!.drawImage(effectIcon(e.id), 0, 0);
      el.style.cssText = `position:absolute;left:${x + 7}px;top:${y + 7}px;width:18px;height:18px;image-rendering:pixelated;`;
      this.list.appendChild(el);
      const nm = mcText(effectLabel(e.id, e.amp), { color: '#ffffff', shadow: true });
      const nmLeft = wide ? x + 28 : x + 28;
      const nmTop = wide ? y + 6 : y + 6;
      nm.style.cssText = `position:absolute;left:${nmLeft}px;top:${nmTop}px;width:${Math.min(nm.width, rw - 32)}px;height:${nm.height}px;image-rendering:pixelated;white-space:nowrap;overflow:hidden;`;
      const tm = mcText(formatDuration(e.dur), { color: '#7f7f7f', shadow: true });
      const tmLeft = wide ? x + 28 : x + 28;
      const tmTop = wide ? y + 16 : y + 16;
      tm.style.cssText = `position:absolute;left:${tmLeft}px;top:${tmTop}px;width:${tm.width}px;height:${tm.height}px;image-rendering:pixelated;`;
      this.list.append(nm, tm);
    });
  }
}
